import { type ChangeEvent, type ComponentProps, type FormEvent, useMemo, useState } from "react";
import { PortableText, type PortableTextComponents } from "@portabletext/react";

import { Button, Checkbox } from "../ui";
import { licenseAgreementZIndex } from "../globals";
import { ArtisLogo } from "../../images/ArtisLogo";
import { useAllLicenseAgreements } from "../../sanity/queries";
import type { Block } from "../../__generated__/sanity/graphql-request";
import { assetRefToSanityUrl } from "../infoSection/utils";
import { useUserAmendExchangeUsage } from "../../data/user";
import { useCurrentUserOrg, useUserId } from "../../context/auth/hooks";

export const agreementTypes = [
  "CME_NYMEX",
  "ICE_FUTURES_EUROPE",
  "ICE_FUTURES_ABU_DHABI",
  "ICE_ENDEX",
  "IDS",
  "TP_ICAP",
  "ARTIS_XC",
  "ARTIS",
  "SGX",
  "ZCE",
] as const;

export type TAgreementType = (typeof agreementTypes)[number];

const replacePlaceholders = (text: string, username: string | undefined, org: string | undefined) => {
  const currentDate = new Date().toISOString().split("T")[0];
  return text
    .replace("[Date]", currentDate)
    .replace("[UserName]", username || "User")
    .replace("[OrganisationName]", org || "Organization");
};

function applyPlaceholders(blocks: Block[] | null | undefined, username: string | undefined, org: string | undefined) {
  if (!blocks) return [];

  return blocks.map((block) => {
    if (block._type !== "block" || !block.children) return block;

    return {
      ...block,
      children: block.children.map((child) => {
        if (child?._type === "span" && child.text) {
          return { ...child, text: replacePlaceholders(child.text, username, org) };
        }
        return child;
      }),
    };
  });
}

const licenseAgreementComponents: PortableTextComponents = {
  block: {
    normal: ({ children }) => <p className="my-2.5">{children}</p>,
    h1: ({ children }) => <h1 className="text-2xl font-semibold my-4">{children}</h1>,
    h2: ({ children }) => <h2 className="text-xl font-semibold my-3">{children}</h2>,
    h3: ({ children }) => <h3 className="text-xl font-semibold my-2.5">{children}</h3>,
    h4: ({ children }) => <h4 className="text-lg font-semibold my-2.5">{children}</h4>,
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-outline pl-4 my-2.5 italic text-foreground-muted">{children}</blockquote>
    ),
  },
  list: {
    bullet: ({ children, value }) => (
      <div className="mb-2" style={{ marginLeft: value.level ? `${value.level * 20}px` : undefined }}>
        <ul className="ml-5 list-disc list-outside pl-1">{children}</ul>
      </div>
    ),
  },
  listItem: {
    bullet: ({ children }) => <li className="mb-2">{children}</li>,
  },
  marks: {
    strong: ({ children }) => <strong>{children}</strong>,
    em: ({ children }) => <em>{children}</em>,
    link: ({ value, children }) => (
      <a href={value?.href} target="_blank" rel="noopener noreferrer" className="text-primary underline">
        {children}
      </a>
    ),
  },
  types: {
    breakline: () => <br />,
    image: ({ value }) => {
      const assetRef = value?.asset?._ref;
      if (!assetRef) return null;
      return <img src={assetRefToSanityUrl(assetRef)} alt="" className="max-w-full my-4" />;
    },
  },
};

const AgreementRenderer = ({
  bodyRaw,
  username,
  org,
}: {
  bodyRaw: Block[] | null | undefined;
  username: string | undefined;
  org: string | undefined;
}) => {
  const value = useMemo(() => applyPlaceholders(bodyRaw, username, org), [bodyRaw, username, org]);

  if (!bodyRaw?.length) {
    return <p>No agreement body available.</p>;
  }

  return (
    <PortableText
      value={value as ComponentProps<typeof PortableText>["value"]}
      components={licenseAgreementComponents}
    />
  );
};

export const LicenceAgreementForm = ({
  username,
  agreementType,
  sourceId,
}: {
  username: string | undefined;
  agreementType: TAgreementType;
  sourceId: number | undefined;
}) => {
  const { data: org } = useCurrentUserOrg();
  const { data: agreementsData, loading: agreementsLoading } = useAllLicenseAgreements();
  const companyName = org?.data?.organisation_by_pk?.sourceBySource.name;
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { mutateAsync: amendMutateAsync } = useUserAmendExchangeUsage();
  const userId = useUserId();
  const agreements = agreementsData?.allLicenseAgreement ?? [];

  const filteredAgreements = agreements.filter(
    (agreement) => agreement.title?.toUpperCase() === agreementType.toUpperCase(),
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setAccepted(event.target.checked);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!userId || !sourceId) return;
    setSubmitting(true);

    const newExchanges = [
      {
        folio_user: userId,
        source: sourceId,
        event: "start",
      },
    ];
    try {
      await amendMutateAsync({ exchangeUsages: newExchanges });

      // waiting for the subscription to update the reduces the likely hood of the same agreement being shown again
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setAccepted(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Error updating user:", error);
    } finally {
      setTimeout(() => {
        setSubmitting(false);
      }, 500);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="min-h-screen bg-background text-foreground">
      <div
        className="relative p-3 w-full md:w-[calc(100%-var(--artis-side-bar-width))] md:left-[var(--artis-side-bar-width)]"
        style={{ zIndex: licenseAgreementZIndex }}
      >
        <div className="w-[100px] -ml-2">
          <ArtisLogo />
        </div>

        <div className="licence-agreements-top">
          <div className="text-component mt-10 max-w-[75ch] mx-auto text-left">
            {!agreementsLoading && (
              <AgreementRenderer bodyRaw={filteredAgreements[0]?.bodyRaw} username={username} org={companyName} />
            )}
            <div className="mt-4 mb-8">
              <Checkbox
                id={"licence-agreement-checkbox"}
                label={`ACCEPT ${
                  !["ARTIS_XC", "TP_ICAP"].includes(agreementType) ? agreementType.replaceAll("_", " ") : ""
                } TERMS`}
                name={agreementType}
                checked={accepted}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
        <div className="licence-agreements-bottom mb-5 max-w-[75ch] mx-auto">
          <Button variant="solid" color="primary" type="submit" disabled={submitting || !accepted || agreementsLoading}>
            {submitting ? "Submitting..." : "Submit"}
          </Button>
        </div>
      </div>
    </form>
  );
};
